import {
  ActionButton,
  Label,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import {
  Navigate,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import type { MouseEventHandler } from "react"
import {
  useCallback,
  useState,
} from "react"
import { RemoveFollowerDialog } from "./RemoveFollowerDialog"
import styles from "./Sharing.module.scss"
import { PageTemplate } from "src/components"
import {
  useOverlayLoader,
  usePolyglot,
} from "src/contexts"
import {
  useDeleteFollow,
  useFollowById,
} from "src/hooks"
import {
  DeleteFollowerErrorToast,
  DeleteFollowerSuccessToast,
} from "src/toasts"
import {
  followToFollowerFull,
  injectProps,
} from "src/utils"

const polyglotPrefix = "pages.data_sharing.manage_follower."

export function ManageFollower() {
  const followId = useParams({
    from: "/private/data_sharing/sharing/$followId/manage_follower",
    select: (s) => s.followId,
  })

  const [
    follow,
    hasLoaded,
  ] = useFollowById(followId)
  const polyglot = usePolyglot()
  const navigate = useNavigate()

  const deleteFollow = useDeleteFollow()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()
  const displayName = follow ? followToFollowerFull(follow) : ""

  const [
    removeFollowerDialogOpen,
    setRemoveFollowerDialogOpen,
  ] =
    useState<boolean>(false)

  const openRemoveFollowerDialog = useCallback(
    () => {
      setRemoveFollowerDialogOpen(true)
    },
    [setRemoveFollowerDialogOpen],
  )

  const removeFollower: MouseEventHandler<HTMLButtonElement> =
    useCallback(
      () => {
        void (async () => {
          setIsOverlayLoaderVisible(true)

          try {
            if (!deleteFollow || !follow) {
              throw new Error("Unable to remove follower")
            }

            await deleteFollow(followId)
            // TODO: fireRemoveFollowerAnalytic()

            const SuccessToastComponent = injectProps(
              DeleteFollowerSuccessToast,
              { displayName: displayName },
            )

            showToast(
              "remove-follower-success-toast",
              SuccessToastComponent,
            )

            await navigate({ to: "/data_sharing/sharing" })
          } catch {
            showToast(
              "remove-follower-error-toast",
              DeleteFollowerErrorToast,
            )
          }

          setIsOverlayLoaderVisible(false)
        })()
      },
      [
        deleteFollow,
        follow,
        displayName,
        showToast,
        navigate,
        followId,
        setIsOverlayLoaderVisible,
      ],
    )

  if (hasLoaded && follow === undefined) {
    return (
      <Navigate
        to="/data_sharing/sharing"
        params={{}}
        search={{}}
        replace
      />
    )
  }

  return (
    <PageTemplate
      user="account"
      title={polyglot.t("components.page_header.pages.manage_follower")}
      backRoutePath="/data_sharing/sharing"
    >
      <div className={styles.container}>
        <div className={styles.manageFollowerDetailsContainer}>
          <Label className={styles.groupLabel}>
            {polyglot.t(`${polyglotPrefix}manage_access_for.title`)}
          </Label>
          <div className={styles.manageFollowerDetailsText}>
            {follow ? followToFollowerFull(follow) : ""}
          </div>
          <div className={styles.manageFollowerDetailsText}>
            {follow?.follower_email}
          </div>
        </div>
        <div className={styles.actionContainer}>
          <div className={styles.manageFollowerDescriptionContainer}>
            <Label>
              {polyglot.t(`${polyglotPrefix}remove_follower.title`)}
            </Label>
            <div className={styles.manageFollowerDescriptionTextContainer}>
              <div className={styles.description}>
                {polyglot.t(`${polyglotPrefix}remove_follower.description_one`)}
              </div>
              <div className={styles.description}>
                {polyglot.t(`${polyglotPrefix}remove_follower.description_two`)}
              </div>
            </div>
          </div>
          <ActionButton
            className={styles.button}
            stylePreset="destructive"
            onClick={openRemoveFollowerDialog}
            id="remove-follower-btn"
          >
            {polyglot.t(`${polyglotPrefix}remove_follower.title`)}
          </ActionButton>
        </div>
        <RemoveFollowerDialog
          open={removeFollowerDialogOpen}
          setOpen={setRemoveFollowerDialogOpen}
          removeFollower={removeFollower}
          displayName={displayName}
        />
      </div>
    </PageTemplate>
  )
}
